/* eslint-disable react/react-in-jsx-scope */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useIdleTimer } from "react-idle-timer";
import { BrowserRouter } from "react-router-dom";
import PageLayout from "./components/PageLayout";
import CssBaseline from "@mui/material/CssBaseline";
import { AppContext } from "./AppContext";
import { PowerBiReport, PowerBiDataset } from "./models/models";
import DashboardWebApi from "./services/DashboardWebApi";

const App = () => {
  const [tenantName, setTenantName] = useState<string>(null);
  const [reports, setReports] = useState<PowerBiReport[]>(null);
  const [datasets, setDatasets] = useState<PowerBiDataset[]>(null);
  const [userName, setUserName] = useState<string>(null);
  const [loginId, setLoginId] = useState<string>(null);
  const [userCanEdit, setUserCanEdit] = useState<boolean>(null);
  const [userCanCreate, setUserCanCreate] = useState<boolean>(null);
  const [workspaceArtifactsLoading, setWorkspaceArtifactsLoading] =
    useState<boolean>(null);
  const logHeader = "[App]";

  let isAuthenticated = DashboardWebApi.IsAuthenticated();
  console.debug(`${logHeader} isAuthenticated = `, isAuthenticated);

  const onIdle = () => {
    console.debug(
      `${logHeader} Session idle time has been exceeded, setting isAuthenticated to false`,
    );
    isAuthenticated = false;
    DashboardWebApi.ResetLoginStatus();
    DashboardWebApi.ResetAuthentication();
    console.debug(`${logHeader} Initiating sign-on based on idle event`);
    globalThis.location.replace(
      `${DashboardWebApi.SamlRoot}InitiateSingleSignOn?relayState=${globalThis.location.href}`,
    );
  };

  const onAction = () => {};

  useIdleTimer({
    onIdle,
    onAction,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
      "focus",
    ],
    timeout: 1000 * 60 * 60,
    throttle: 500,
  });

  const refreshEmbeddingData = useCallback(async () => {
    const viewModel = await DashboardWebApi.GetEmbeddingData();
    setReports(viewModel.reports);
  }, []); // No dependencies, function behavior never changes

  useEffect(() => {
    const getEmbeddingDataAsync = async () => {
      setWorkspaceArtifactsLoading(true);
      const viewModel = await DashboardWebApi.GetEmbeddingData();
      if (viewModel !== null) {
        setTenantName(viewModel.tenantName);
        setReports(viewModel.reports);
        setDatasets(viewModel.datasets);
        setUserCanEdit(viewModel.userCanEdit);
        setUserCanCreate(viewModel.userCanCreate);
      }

      const user = DashboardWebApi.GetUser();
      setLoginId(user.LoginId);
      setUserName(user.UserName);

      setWorkspaceArtifactsLoading(false);
    };
    if (isAuthenticated) {
      getEmbeddingDataAsync();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      console.debug(
        `${logHeader} Creating interval timer for access token refresh`,
      );
      setInterval(
        () => {
          const accessToken = DashboardWebApi.GetAccessToken();
          if (accessToken === "") {
            console.error(
              `${logHeader} Access token is invalid/missing, setting isAuthenticated to false`,
            );
            isAuthenticated = false;
            DashboardWebApi.ResetLoginStatus();
            DashboardWebApi.ResetAuthentication();
            console.debug(`${logHeader} Initiating sign-on for token refresh`);
            globalThis.location.replace(
              `${DashboardWebApi.SamlRoot}InitiateSingleSignOn?relayState=${globalThis.location.href}`,
            );
          } else {
            console.debug(`${logHeader} Renew access token`);
            DashboardWebApi.GetRenewedJWT();
          }
        },
        5 * 60 * 1000,
      );
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      DashboardWebApi.ResetLoginStatus();
      const promise = DashboardWebApi.GetAuthorization();
      promise
        .then((accessToken) => {
          if (accessToken != null) {
            console.debug(`${logHeader} Login user with access token`);
            const promise = DashboardWebApi.LoginUser(accessToken);
            promise.then((status) => {
              console.debug(`${logHeader} Returned from user login`);
            });
          }
        })
        .catch((err) => console.error(err));
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const contextValue = useMemo(
    () => ({
      embeddingData: {
        tenantName: tenantName,
        reports: reports,
        datasets: datasets,
        user: userName,
        userCanEdit: userCanEdit,
        userCanCreate: userCanCreate,
        workspaceArtifactsLoading: workspaceArtifactsLoading,
        loginId: loginId,
      },
      refreshEmbeddingData: refreshEmbeddingData,
    }),
    [
      tenantName,
      reports,
      datasets,
      userName,
      userCanEdit,
      userCanCreate,
      workspaceArtifactsLoading,
      loginId,
      refreshEmbeddingData,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>
      <CssBaseline />
      <BrowserRouter>
        <PageLayout />
      </BrowserRouter>
    </AppContext.Provider>
  );
};

export default App;
